import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import WarningBanner from '@/components/WarningBanner'
import { usePageAccess } from '@/hooks/usePageAccess'
import {
  getAllocationMonthReadiness,
  updateAllocationMonthNotes,
  updateAllocationMonthReadiness,
} from '@/services/waterfall'
import type { AllocationMonthReadinessResponse } from '@/types/waterfall'
import { formatApiError } from '@/utils/apiError'
import {
  blockingRequiredComplete,
  buildNetIncomeDriftMessage,
  canPostJeBeforeQboFollowUp,
  formatReadinessCheckedByTooltip,
  plainReadinessLabel,
  readinessCompletionLabel,
  readinessNeedsQboFollowUp,
  readinessPillTone,
  recomputeReadinessFromItems,
  shouldShowReadinessIncompleteFooter,
  splitReadinessLabelSegments,
  type AllocationReadinessChange,
} from './allocationMonthReadinessUtils'
import { isPacPriorYearCloseChecked } from './pacYearEndCloseUtils'

type NotesSaveStatus = 'idle' | 'saving' | 'saved' | 'error'

type Props = {
  month: string
  embedded?: boolean
  showNotes?: boolean
  monthPicker?: ReactNode
  onReadinessChange?: (state: AllocationReadinessChange) => void
}

function toReadinessChange(
  items: AllocationMonthReadinessResponse['items'],
  netIncomeDrift: boolean,
  canPostJe?: boolean,
): AllocationReadinessChange {
  const recomputed = recomputeReadinessFromItems(items, netIncomeDrift)
  return {
    ...recomputed,
    canPostJe: canPostJe ?? recomputed.canPostJe,
    pacPriorYearClosePosted: isPacPriorYearCloseChecked(items),
  }
}

function ReadinessLabelText({ label }: { label: string }) {
  return (
    <>
      {splitReadinessLabelSegments(label).map((segment, index) =>
        segment.type === 'code' ? (
          <code
            key={`${segment.type}-${index}`}
            className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] text-gray-800"
          >
            {segment.value}
          </code>
        ) : (
          <span key={`${segment.type}-${index}`}>{segment.value}</span>
        ),
      )}
    </>
  )
}

const PILL_TONE_CLASS: Record<ReturnType<typeof readinessPillTone>, string> = {
  amber: 'bg-amber-50 text-amber-900 ring-amber-100',
  emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-100',
}

export default function AllocationMonthReadiness({
  month,
  embedded = false,
  showNotes = true,
  monthPicker,
  onReadinessChange,
}: Props) {
  const { canEdit } = usePageAccess('distributions')
  const [data, setData] = useState<AllocationMonthReadinessResponse | null>(null)
  const dataRef = useRef<AllocationMonthReadinessResponse | null>(null)
  dataRef.current = data
  const onReadinessChangeRef = useRef(onReadinessChange)
  onReadinessChangeRef.current = onReadinessChange
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(true)
  const [togglingIds, setTogglingIds] = useState<Set<string>>(() => new Set())
  const [notes, setNotes] = useState('')
  const [notesSaveStatus, setNotesSaveStatus] = useState<NotesSaveStatus>('idle')
  const notesDraftRef = useRef('')
  const notesDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notesSaveGenRef = useRef(0)
  const monthRef = useRef(month)

  const persistNotes = useCallback(async (targetMonth: string, notesText: string, gen: number) => {
    const isCurrent = () => gen === notesSaveGenRef.current && targetMonth === monthRef.current
    if (isCurrent()) {
      setNotesSaveStatus('saving')
    }
    try {
      const result = await updateAllocationMonthNotes(targetMonth, notesText)
      if (!isCurrent()) return
      setNotesSaveStatus('saved')
      setData((prev) => (prev ? { ...prev, notes: result.notes } : prev))
    } catch (e) {
      if (!isCurrent()) return
      setNotesSaveStatus('error')
      setError(formatApiError(e, 'Failed to save notes'))
    }
  }, [])

  const scheduleNotesSave = useCallback(
    (targetMonth: string, notesText: string) => {
      if (notesDebounceRef.current) clearTimeout(notesDebounceRef.current)
      const gen = notesSaveGenRef.current
      notesDebounceRef.current = setTimeout(() => {
        notesDebounceRef.current = null
        void persistNotes(targetMonth, notesText, gen)
      }, 500)
    },
    [persistNotes],
  )

  const flushPendingNotes = (targetMonth: string, notesText: string) => {
    if (!notesDebounceRef.current) return
    clearTimeout(notesDebounceRef.current)
    notesDebounceRef.current = null
    if (targetMonth) {
      void updateAllocationMonthNotes(targetMonth, notesText)
    }
  }

  useEffect(() => {
    return () => {
      flushPendingNotes(monthRef.current, notesDraftRef.current)
    }
  }, [])

  useEffect(() => {
    if (!month) return

    const previousMonth = monthRef.current
    if (previousMonth !== month) {
      flushPendingNotes(previousMonth, notesDraftRef.current)
      notesSaveGenRef.current += 1
      monthRef.current = month
      setNotes('')
      notesDraftRef.current = ''
      setNotesSaveStatus('idle')
    }

    let cancelled = false
    setData(null)
    setError(null)
    setLoading(true)

    async function loadReadiness() {
      try {
        const next = await getAllocationMonthReadiness(month)
        if (cancelled) return

        setData(next)
        setNotes(next.notes ?? '')
        notesDraftRef.current = next.notes ?? ''
        setNotesSaveStatus('idle')
        setLoading(false)
        const loaded = toReadinessChange(
          next.items,
          next.net_income_drift,
          canPostJeBeforeQboFollowUp({
            loggedNetIncome: next.logged_net_income,
            canPostJe: next.can_post_je,
          }),
        )
        onReadinessChangeRef.current?.(loaded)
        if (!next.all_required_checked || next.net_income_drift) {
          setExpanded(true)
        }

        if (!readinessNeedsQboFollowUp(next.logged_net_income)) {
          return
        }

        try {
          const withQbo = await getAllocationMonthReadiness(month, { includeQbo: true })
          if (cancelled) return

          let mergedChange: AllocationReadinessChange | null = null
          setData((prev) => {
            if (!prev) return prev
            const recomputed = toReadinessChange(prev.items, withQbo.net_income_drift)
            mergedChange = recomputed
            return {
              ...prev,
              qbo_net_income: withQbo.qbo_net_income,
              net_income_drift: withQbo.net_income_drift,
              logged_net_income: withQbo.logged_net_income,
              allocations_after_count: withQbo.allocations_after_count,
              checked_count: recomputed.checkedCount,
              all_required_checked: recomputed.allRequiredChecked,
              can_post_je: recomputed.canPostJe,
              notes: withQbo.notes ?? prev.notes,
            }
          })
          if (mergedChange) {
            onReadinessChangeRef.current?.(mergedChange)
          }
          if (withQbo.net_income_drift) {
            setExpanded(true)
          }
        } catch (e) {
          if (cancelled) return
          setError(formatApiError(e, 'Failed to check QuickBooks net income'))
        }
      } catch (e) {
        if (cancelled) return
        setError(formatApiError(e, 'Failed to load month readiness'))
        setData(null)
        setLoading(false)
        onReadinessChangeRef.current?.({
          canPostJe: false,
          allRequiredChecked: false,
          blockingComplete: false,
          checkedCount: 0,
          pacPriorYearClosePosted: false,
        })
      }
    }

    void loadReadiness()
    return () => {
      cancelled = true
    }
  }, [month])

  const handleNotesChange = (value: string) => {
    setNotes(value)
    notesDraftRef.current = value
    if (!canEdit || !month) return
    setNotesSaveStatus('idle')
    scheduleNotesSave(month, value)
  }

  const handleToggle = async (checkId: string, checked: boolean) => {
    const current = dataRef.current
    if (!canEdit || !month || !current) return
    if (togglingIds.has(checkId)) return

    const previousItem = current.items.find((item) => item.id === checkId)
    if (!previousItem) return

    const optimisticItems = current.items.map((item) =>
      item.id === checkId
        ? {
            ...item,
            checked,
            checked_at: null,
            checked_by: null,
          }
        : item,
    )
    const recomputed = toReadinessChange(optimisticItems, current.net_income_drift)
    const optimisticData: AllocationMonthReadinessResponse = {
      ...current,
      items: optimisticItems,
      checked_count: recomputed.checkedCount,
      all_required_checked: recomputed.allRequiredChecked,
      can_post_je: recomputed.canPostJe,
    }

    dataRef.current = optimisticData
    setData(optimisticData)
    onReadinessChange?.(recomputed)
    setTogglingIds((prev) => new Set(prev).add(checkId))
    setError(null)

    try {
      const next = await updateAllocationMonthReadiness({
        month,
        check_id: checkId,
        checked,
      })
      const serverItem = next.items.find((item) => item.id === checkId)
      if (!serverItem) return

      let mergedChange: AllocationReadinessChange | null = null
      setData((prev) => {
        if (!prev) return prev
        const mergedItems = prev.items.map((item) =>
          item.id === checkId ? serverItem : item,
        )
        const merged = toReadinessChange(mergedItems, prev.net_income_drift)
        mergedChange = merged
        return {
          ...prev,
          items: mergedItems,
          checked_count: merged.checkedCount,
          all_required_checked: merged.allRequiredChecked,
          can_post_je: merged.canPostJe,
        }
      })
      if (mergedChange) {
        onReadinessChange?.(mergedChange)
      }
    } catch (e) {
      let revertedChange: AllocationReadinessChange | null = null
      setData((prev) => {
        if (!prev) return prev
        const revertedItems = prev.items.map((item) =>
          item.id === checkId
            ? {
                ...item,
                checked: previousItem.checked,
                checked_at: previousItem.checked_at,
                checked_by: previousItem.checked_by,
              }
            : item,
        )
        const reverted = toReadinessChange(revertedItems, prev.net_income_drift)
        revertedChange = reverted
        return {
          ...prev,
          items: revertedItems,
          checked_count: reverted.checkedCount,
          all_required_checked: reverted.allRequiredChecked,
          can_post_je: reverted.canPostJe,
        }
      })
      if (revertedChange) {
        onReadinessChange?.(revertedChange)
      }
      setError(formatApiError(e, 'Failed to update readiness check'))
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev)
        next.delete(checkId)
        return next
      })
    }
  }

  const pillTone = readinessPillTone(data?.all_required_checked ?? false)
  const driftMessage =
    data?.net_income_drift === true
      ? buildNetIncomeDriftMessage(
          data.logged_net_income,
          data.qbo_net_income,
          data.allocations_after_count,
        )
      : null

  const notesSaveStatusLabel =
    notesSaveStatus === 'saving'
      ? 'Saving…'
      : notesSaveStatus === 'saved'
        ? 'Saved'
        : notesSaveStatus === 'error'
          ? 'Save failed'
          : null

  const outerClassName = embedded
    ? ''
    : 'rounded-lg border border-gray-200 bg-white shadow-sm'
  const headerClassName = embedded
    ? 'px-0 pt-4 pb-3'
    : 'border-b border-gray-100 px-4 py-3'
  const bodyPadClass = embedded ? 'px-0 py-3' : 'px-4 py-3'

  const checklistBodyId = `allocation-month-readiness-body-${month || 'none'}`

  return (
    <section className={outerClassName}>
      <div
        className={`flex flex-wrap items-center justify-between gap-3 ${headerClassName}`}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="container-title">Month readiness checklist</h3>
          {data ? (
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${PILL_TONE_CLASS[pillTone]}`}
            >
              {readinessCompletionLabel(data.checked_count, data.required_count)}
            </span>
          ) : null}
        </div>
        {loading ? <span className="text-xs text-gray-500">Loading…</span> : null}
      </div>

      <div className={`space-y-3 ${bodyPadClass}`}>
        {monthPicker ? <div>{monthPicker}</div> : null}

        <button
          type="button"
          className="flex w-full items-center gap-2 text-left"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={checklistBodyId}
        >
          <ChevronDownIcon
            className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${expanded ? '' : '-rotate-90'}`}
            aria-hidden="true"
          />
          <span className="text-sm font-medium text-gray-700">
            {showNotes ? 'Checklist and notes' : 'Checklist'}
          </span>
        </button>

        {expanded ? (
          <div id={checklistBodyId} className="space-y-3">
          {error ? (
            <p className="text-xs text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          {loading && !data ? (
            <p className="text-xs text-gray-500">Loading readiness checks…</p>
          ) : null}

          {data ? (
            <div className="grid gap-6 md:grid-cols-2">
              <ul className="space-y-2">
                {data.items.map((item) => {
                  const disabled = !canEdit
                  const optionalMarker = !item.required ? (
                    <span className="ml-1 text-xs text-gray-500">(optional)</span>
                  ) : null
                  const checkedMeta = item.checked
                    ? formatReadinessCheckedByTooltip(item.checked_by, item.checked_at)
                    : null
                  return (
                    <li key={item.id}>
                      <div
                        className={`flex items-start gap-2 text-sm text-gray-800 ${
                          canEdit ? '' : 'cursor-default'
                        }`}
                      >
                        <button
                          type="button"
                          role="checkbox"
                          aria-checked={item.checked}
                          aria-label={plainReadinessLabel(item.label)}
                          disabled={disabled}
                          className={`mt-0.5 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed ${
                            canEdit ? '' : 'cursor-default'
                          }`}
                          onClick={() => {
                            if (togglingIds.has(item.id)) return
                            void handleToggle(item.id, !item.checked)
                          }}
                        >
                          {item.checked ? (
                            <CheckCircleIcon
                              className="h-5 w-5 -m-0.5 text-emerald-600"
                              aria-hidden="true"
                            />
                          ) : (
                            <span
                              className="block h-4 w-4 rounded-full border border-dashed border-gray-500"
                              aria-hidden="true"
                            />
                          )}
                        </button>
                        <div className="min-w-0 flex-1">
                          <span>
                            <ReadinessLabelText label={item.label} />
                            {optionalMarker}
                          </span>
                          {checkedMeta ? (
                            <span className="mt-0.5 block text-xs text-gray-500">
                              {checkedMeta}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>

              {showNotes ? (
                <div className="flex min-h-0 flex-col">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <label
                      htmlFor={`allocation-month-notes-${month}`}
                      className="text-sm font-medium text-gray-700"
                    >
                      Notes
                    </label>
                    {notesSaveStatusLabel ? (
                      <span
                        className={`text-xs ${
                          notesSaveStatus === 'error'
                            ? 'text-red-600'
                            : notesSaveStatus === 'saved'
                              ? 'text-emerald-700'
                              : 'text-gray-500'
                        }`}
                      >
                        {notesSaveStatus === 'saving' ? (
                          <span className="inline-flex items-center gap-1">
                            <ArrowPathIcon className="h-3 w-3 animate-spin" />
                            {notesSaveStatusLabel}
                          </span>
                        ) : (
                          notesSaveStatusLabel
                        )}
                      </span>
                    ) : null}
                  </div>
                  <textarea
                    id={`allocation-month-notes-${month}`}
                    className="min-h-[12rem] w-full flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    rows={8}
                    value={notes}
                    disabled={!canEdit}
                    onChange={(e) => handleNotesChange(e.target.value)}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          {driftMessage ? <WarningBanner message={driftMessage} /> : null}

          {data &&
          shouldShowReadinessIncompleteFooter(blockingRequiredComplete(data.items)) ? (
            <p className="text-xs text-amber-900">
              Complete all readiness checks before calculating the split or creating a journal entry.
            </p>
          ) : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}
